/**
 * Extracts user-friendly error message from Yapily API errors
 *
 * Yapily errors contain nested institution error messages that need to be parsed.
 * This function extracts all error messages using regex and combines them.
 *
 * @param error - The error object from axios
 * @returns User-friendly error message(s) or null if unable to parse
 */
type YapilyErrorResponse = {
  name?: string;
  message?: string;
  details?: {
    institutionError?: {
      errorMessage?: string;
    };
  };
};

export const parseYapilyError = (error: unknown): string | null => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const responseData = (error as any)?.response?.data as YapilyErrorResponse;

    if (!responseData) {
      return null;
    }

    if (responseData.name !== 'YapilyError') {
      return responseData.message || null;
    }

    // Check if this is a Yapily error with institution error details
    if (responseData.details?.institutionError?.errorMessage) {
      const rawErrorMessage = responseData.details.institutionError.errorMessage;

      const errorMatches = [
        ...rawErrorMessage.matchAll(/"text":"([^"]+)"/g),
        ...rawErrorMessage.matchAll(/"psuMessage":"([^"]+)"/g),
      ];

      const errorMessages = errorMatches.map((match) => match[1]).filter((msg) => msg && msg.trim().length > 0);

      if (errorMessages.length > 0) {
        return errorMessages.join('. ');
      }
    }

    return responseData.message || null;
  } catch {
    return null;
  }
};
